import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface Dimension {
  name: string;
  description: string;
  icon: string;
  color: string;
}

const DIMENSIONS: Dimension[] = [
  { name: '第1-3维：空间', description: '长度、宽度、高度 — 我们日常感知的三维空间', icon: '📐', color: '#4ECDC4' },
  { name: '第4维：时间', description: '时空连续体的第四个坐标，事件的先后顺序', icon: '⏱️', color: '#FFD700' },
  { name: '第5-6维：可能性', description: '其他可能的宇宙分支和初始条件的变化空间', icon: '🌀', color: '#FF6B35' },
  { name: '第7-10维：终极对称', description: '超弦振动模式的完整数学描述所需的额外维度', icon: '✨', color: '#9B59B6' },
  { name: '第11维：M理论', description: '统一五种弦理论的膜理论，弦在膜上端点可移动', icon: '🌌', color: '#667EEA' },
];

const KEY_CONCEPTS = [
  { title: '弦', desc: '万物基本单元是一维振动弦', color: '#4ECDC4' },
  { title: '振动模式', desc: '不同频率产生不同粒子', color: '#FFD700' },
  { title: '额外维度', desc: '紧致化的6-7个隐藏维度', color: '#FF6B35' },
  { title: '超对称', desc: '每个粒子都有超对称伙伴', color: '#9B59B6' },
];

const VibratingString: React.FC<{
  frame: number;
  x: number;
  y: number;
  width: number;
  mode: number;
  color: string;
  opacity: number;
}> = ({ frame, x, y, width, mode, color, opacity }) => {
  const points: string[] = [];
  const segments = 60;
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const px = x + t * width;
    const amplitude = 25 * Math.sin(frame * 0.08 * (mode + 1));
    const py = y + amplitude * Math.sin(t * Math.PI * (mode + 1));
    points.push(`${px},${py}`);
  }

  return (
    <polyline
      points={points.join(' ')}
      fill="none"
      stroke={color}
      strokeWidth={3}
      opacity={opacity}
      strokeLinecap="round"
    />
  );
};

export const StringTheoryAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleProg = spring({ frame, fps, config: { damping: 14, stiffness: 120 } });

  return (
    <AbsoluteFill
      style={{
        background: 'radial-gradient(ellipse at 50% 40%, #0f0a2e 0%, #050510 60%, #020208 100%)',
        fontFamily: 'system-ui, -apple-system, PingFang SC, sans-serif',
        overflow: 'hidden',
      }}
    >
      {/* Animated background strings */}
      <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.12 }}>
        {[0, 1, 2, 3, 4].map((mode) => (
          <VibratingString
            key={mode}
            frame={frame}
            x={100}
            y={200 + mode * 160}
            width={1720}
            mode={mode}
            color={DIMENSIONS[mode]?.color ?? '#fff'}
            opacity={0.4}
          />
        ))}
      </svg>

      <div
        style={{
          position: 'absolute',
          top: 36,
          left: 60,
          opacity: titleProg,
          transform: `translateY(${interpolate(titleProg, [0, 1], [-30, 0])}px)`,
        }}
      >
        <h1
          style={{
            fontSize: 68,
            fontWeight: 800,
            margin: 0,
            background: 'linear-gradient(135deg, #9B59B6, #4ECDC4, #FFD700)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          弦理论入门
        </h1>
        <p style={{ fontSize: 28, margin: '8px 0 0', color: 'rgba(255,255,255,0.4)' }}>
          String Theory · 寻找万物理论的终极候选
        </p>
      </div>

      {/* Vibrating string demo */}
      <div
        style={{
          position: 'absolute',
          top: 300,
          left: 60,
          width: 550,
          height: 300,
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 20,
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '16px 20px', fontSize: 28, color: 'rgba(255,255,255,0.4)' }}>
          🎵 弦的振动模式 → 不同粒子
        </div>
        <svg width={550} height={240}>
          {[0, 1, 2].map((mode) => {
            const modeProg = interpolate(frame, [30 + mode * 20, 50 + mode * 20], [0, 1], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            });
            const labels = ['基态 (光子)', '一阶 (电子)', '二阶 (夸克)'];
            return (
              <g key={mode} opacity={modeProg}>
                <VibratingString
                  frame={frame}
                  x={40}
                  y={50 + mode * 75}
                  width={340}
                  mode={mode}
                  color={['#4ECDC4', '#FFD700', '#FF6B35'][mode]}
                  opacity={1}
                />
                <text
                  x={400}
                  y={55 + mode * 75}
                  fontSize={22}
                  fill={['#4ECDC4', '#FFD700', '#FF6B35'][mode]}
                >
                  {labels[mode]}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Dimension cards — 2 per row grid */}
      <div
        style={{
          position: 'absolute',
          top: 300,
          left: 650,
          right: 55,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 16,
        }}
      >
        {DIMENSIONS.map((dim, i) => {
          const dimProg = spring({
            frame: frame - 60 - i * 10,
            fps,
            config: { damping: 14, stiffness: 120 },
          });

          return (
            <div
              key={dim.name}
              style={{
                width: 'calc(50% - 8px)',
                padding: '20px 24px',
                background: `${dim.color}08`,
                border: `2px solid ${dim.color}30`,
                borderRadius: 16,
                opacity: dimProg,
                transform: `translateY(${interpolate(dimProg, [0, 1], [20, 0])}px)`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: 38 }}>{dim.icon}</span>
                <h3 style={{ fontSize: 34, fontWeight: 700, color: dim.color, margin: 0 }}>
                  {dim.name}
                </h3>
              </div>
              <p style={{ fontSize: 26, color: '#E5E7EB', lineHeight: 1.5, margin: 0 }}>
                {dim.description}
              </p>
            </div>
          );
        })}
      </div>

      {/* Key concepts */}
      <div
        style={{
          position: 'absolute',
          bottom: 60,
          left: 60,
          right: 55,
          display: 'flex',
          gap: 16,
        }}
      >
        {KEY_CONCEPTS.map((c, i) => {
          const cProg = spring({
            frame: frame - 150 - i * 8,
            fps,
            config: { damping: 14, stiffness: 120 },
          });

          return (
            <div
              key={c.title}
              style={{
                flex: 1,
                padding: '14px 18px',
                background: `${c.color}08`,
                border: `1px solid ${c.color}25`,
                borderRadius: 14,
                opacity: cProg,
              }}
            >
              <div style={{ fontSize: 27, fontWeight: 700, color: c.color }}>{c.title}</div>
              <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
                {c.desc}
              </div>
            </div>
          );
        })}
      </div>

    </AbsoluteFill>
  );
};
